import styled, { css } from 'styled-components';

import { dotStyles, moveElement } from 'shared/components/DotSection/Styled';

import Image from 'next/image';
import { Description, Header, Section, StyledH1 } from './Styled';

const StyledSection = styled(Section)`
  margin-bottom: 0;
  padding-bottom: 0;
`;

const MainContent = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  justify-content: space-between;
  min-height: calc(100vh - 8rem);
  position: relative;

  @media ${({ theme }) => theme.DEVICE.XL} {
    overflow: unset;
  }
`;

const AnimatedDotSmall = styled.div`
  left: 20%;
  ${dotStyles};
  width: 30.4em;
  height: 30.4em;
  background: #fc8000;
  animation: ${moveElement} 12s ease infinite;
`;

const AnimatedDotBig = styled.div`
  ${dotStyles};
  animation: ${moveElement} 18s ease infinite;
  right: 30%;
  width: 57.7em;
  height: 57.7em;
  background: #ffad33;
`;

const ContentContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 1.6rem 2.4rem 0 2.4rem;

  @media ${({ theme }) => theme.DEVICE.XL} {
    margin: 0 auto;
  }
`;

type ImageContainerProps = {
  maxWidth: number;
  isMobileVersion: boolean;
  isCustom: boolean;
};

const ImageContainer = styled.div<ImageContainerProps>`
  margin: 0 auto;
  ${({ isCustom }) =>
    isCustom &&
    css`
      width: 150%;
      transform: translateX(-25%);
    `}

  @media (max-width: ${({ theme }) => theme.BREAKPOINT.XL}) {
    ${({ isMobileVersion }) =>
      isMobileVersion &&
      css`
        display: none;
      `}
  }

  @media ${({ theme }) => theme.DEVICE.XL} {
    width: 100%;
    transform: translateX(0);
    height: auto;
  }
  width: 100%;
  max-width: ${({ maxWidth }) => `${maxWidth}px`};
`;

const ImageMobileContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  width: 100%;

  @media ${({ theme }) => theme.DEVICE.XL} {
    display: none;
  }
`;

type HeaderSectionProps = {
  header: string;
  headerColored?: string;
  dsc: string;
  src?: string;
  width?: number;
  height?: number;
  withAnimatedDots?: boolean;
  isCustom?: boolean;
  mobileSrc?: string;
  mobileWidth?: number;
  mobileHeight?: number;
  isRevert?: boolean;
};

function MainSection({
  headerColored,
  header,
  dsc,
  src,
  width,
  height,
  isCustom = false,
  withAnimatedDots = false,
  mobileSrc,
  mobileWidth,
  mobileHeight,
  isRevert = false,
}: HeaderSectionProps): JSX.Element {
  return (
    <StyledSection>
      <MainContent>
        {withAnimatedDots ? (
          <>
            <AnimatedDotBig />
            <AnimatedDotSmall />
          </>
        ) : null}
        <ContentContainer>
          <Header>
            {isRevert ? (
              <>
                <StyledH1>
                  {header}
                  <br />
                  <span>{headerColored}</span>
                </StyledH1>
              </>
            ) : (
              <>
                <StyledH1 isColored={!!headerColored}>
                  {headerColored || header}
                  {headerColored && (
                    <>
                      <br />
                      <span>{header}</span>
                    </>
                  )}
                </StyledH1>
              </>
            )}
          </Header>
          <Description>{dsc}</Description>
        </ContentContainer>
        {src ? (
          <ImageContainer maxWidth={width as number} isCustom={isCustom} isMobileVersion={!!mobileSrc}>
            <Image
              src={src}
              alt="trustmate reviews"
              width={width}
              height={height}
              quality="100"
              priority
              layout="responsive"
            />
          </ImageContainer>
        ) : null}
        {mobileSrc ? (
          <ImageMobileContainer>
            <Image
              src={mobileSrc}
              alt="trustmate reviews"
              width={mobileWidth}
              height={mobileHeight}
              quality="100"
              priority
            />
          </ImageMobileContainer>
        ) : null}
      </MainContent>
    </StyledSection>
  );
}

export default MainSection;
